import React, { useEffect, useMemo } from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { ManageEventNavigation } from '../shared/navigation/navigation';
import AssetsViewModel, { AssetsDefaultConfiguration } from './assets.viewmodel';
import { MultipleUploadInput } from '../shared/upload-button';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { ITheadSort, ListLayout } from '../../../global/tables/table-list-layout';
import TableLoadingRow from '../../../global/tables/table-loading-row';
import { TGetReportsSortKeys } from '../../../../api/reports.dao';
import { AssetsListItem } from './assets-list-item/assets-list-item';
import { Category } from '../../../../api/assets.dao';
import { SUPPORTED_ASSETS_TYPES } from '../../../../constants/file.constants';
import { AssetsListOrganizationItem } from './assets-list-item/assets-list-organization-item';
import { Paging } from '../../../global/_paging/paging-container';
import { useAssetsDragAndDrop } from './use-assets-drag-and-drop';
export { droppableAsset } from './use-assets-drag-and-drop';
import {
    ASSETS_UPLOAD_DATE_TIME_TEXT,
    ASSETS_FILE_NAME_TEXT,
    ASSETS_LINK_TEXT,
    ASSETS_DRAG_DROP_HINT_TEXT,
    ASSETS_FILE_TYPE_TEXT,
    ASSETS_UPLOAD_FILES_TEXT,
} from './assets.text';

interface IProps {
    vm: AssetsViewModel;
    intl: IntlShape;
}

const AssetsPresentation = injectIntl(mobxReact.observer((props: IProps) => {
    const {
        vm,
        intl,
    } = props;

    const {
        isLoading,
        accountId,
        eventId,
        collapsedGroups,
        onCollapseGroup,
        onExpandGroup,
        selectedFiles,
        fileError,
        uploadAssets,
        isUploadLoading,
        assetsSpeakersGroupedList,
        isLoadingOrganizations,
        getPagingVM,
        onDeleteAsset,
        onDeleteSpeaker,
        assetsList,
        uploadProfileImage,
        setSpeakerBiography,
        isLinkCreating,
        createLink,
        onDeleteAssetDownloadLink
    } = vm;

    const { draggingFile, currentDroppableElementId, isDraggingOverPage, handleDragStart, handleDragEnd } =
        useAssetsDragAndDrop(vm);

    const theads = useMemo((): ITheadSort<TGetReportsSortKeys>[] => [
        { title: intl.formatMessage(ASSETS_UPLOAD_DATE_TIME_TEXT), width: 220 },
        { title: intl.formatMessage(ASSETS_FILE_NAME_TEXT) },
        { title: intl.formatMessage(ASSETS_LINK_TEXT) },
    ], [intl]);

    const updateAttendeePermission = hasPermission(Permission.UPDATE_ATTENDEE);
    const uploadEventAssetsPermission = hasPermission(Permission.UPLOAD_EVENT_ASSETS);
    const deleteAssetDownloadLinkPermission = hasPermission(Permission.DELETE_ASSET_DOWNLOAD_LINK);

    return (
        <div>
            <section className='horizontal-row horizontal-row--with-bottom-margin event-tabs'>
                <ManageEventNavigation />
                <div className='horizontal-row__align-to-row-end'>
                    <div className='buttons-block'>
                        {hasPermission(Permission.UPLOAD_EVENT_ASSETS) && (
                            <>
                                <span className='assets-upload-hint'>
                                    <FormattedMessage
                                        {...ASSETS_DRAG_DROP_HINT_TEXT}
                                        values={{ value: intl.formatMessage(ASSETS_FILE_TYPE_TEXT) }}
                                    />
                                </span>
                                <MultipleUploadInput
                                    onChange={uploadAssets}
                                    acceptedFileTypes={SUPPORTED_ASSETS_TYPES}
                                    className='default-button'
                                    title={<FormattedMessage
                                        {...ASSETS_UPLOAD_FILES_TEXT}
                                        values={{ prefix: '+' }}
                                    />}
                                    type='default'
                                />
                            </>
                        )}
                    </div>
                </div>
            </section>
            <section className='fixed-list-layout assets-layout'>
                <div>
                    <ListLayout
                        sortOptions={{
                            theads: theads,
                            sortRows: vm.setSortParams,
                            sortOrder: vm.sortOrder,
                            sortField: vm.sortField
                        }}
                    >
                        {isLoading() && <TableLoadingRow colSpan={theads.length} className='assets-layout__loading' />}

                        {!isLoading() &&
                            AssetsDefaultConfiguration.map((asset, index) => {
                                return (
                                    <AssetsListItem
                                        onCollapseGroup={onCollapseGroup}
                                        onExpandGroup={onExpandGroup}
                                        collapsedGroups={collapsedGroups}
                                        categoryGroup={asset}
                                        key={index}
                                        colSpan={theads.length}
                                        selectedFiles={selectedFiles}
                                        handleUpdateAssets={uploadAssets}
                                        assetsList={assetsList}
                                        isUploadLoading={isUploadLoading}
                                        accountId={accountId}
                                        eventId={eventId}
                                        onDeleteAsset={onDeleteAsset}
                                        handleDragStart={handleDragStart}
                                        draggingFile={draggingFile}
                                        handleDragEnd={handleDragEnd}
                                        uploadEventAssetsPermission={uploadEventAssetsPermission}
                                        updateAttendeePermission={updateAttendeePermission}
                                        protectedCategory={asset.type === Category.UNCATEGORIZED}
                                        isLinkCreating={isLinkCreating}
                                        createLink={createLink}
                                        onDeleteAssetDownloadLink={onDeleteAssetDownloadLink}
                                        deleteAssetDownloadLinkPermission={deleteAssetDownloadLinkPermission}
                                        isDraggingOverPage={isDraggingOverPage}
                                        hasFiles={vm.hasFiles}
                                        fileError={fileError}
                                    />
                                );
                            })
                        }

                    </ListLayout>
                </div>

                {isLoadingOrganizations() && (
                    <table className='table-list-layout w-100'>
                        <tbody className='table-list-layout__tbody'>
                            <TableLoadingRow colSpan={1} className='assets-layout__loading' />
                        </tbody>
                    </table>
                )}
                {!isLoadingOrganizations() && assetsSpeakersGroupedList?.length > 0 &&
                    assetsSpeakersGroupedList.map((organization, index) => {
                        const group = { type: String(organization.organizationId), label: organization.name };
                        return (
                            <AssetsListOrganizationItem
                                onCollapseGroup={onCollapseGroup}
                                onExpandGroup={onExpandGroup}
                                collapsedGroups={collapsedGroups}
                                categoryGroup={group}
                                key={index}
                                assetsSpeakersGroupedList={organization.speakers}
                                accountId={accountId}
                                eventId={eventId}
                                organization={organization}
                                uploadProfileImage={uploadProfileImage}
                                onDeleteAsset={onDeleteSpeaker}
                                currentDroppableElementId={currentDroppableElementId}
                                updateAttendeePermission={updateAttendeePermission}
                                setSpeakerBiography={setSpeakerBiography}
                            />
                        );
                    })
                }

                {!isLoadingOrganizations() && <Paging pagingVM={getPagingVM()} />}
            </section>
        </div>
    );
}));

const AssetsContainer = (props: { vm: AssetsViewModel }) => {
    const { vm } = props;
    useEffect(() => {
        vm.initialize();
        return () => {
            vm.resetValues();
        };
    }, []);

    return <AssetsPresentation vm={vm} />;
};


export { AssetsContainer as AssetsList, AssetsPresentation };